import { useState, useEffect, useCallback } from 'react';

const CACHE_KEY = 'lincal-offline-cache';

/**
 * Hook to manage offline cache for calendar data
 * Caches the last loaded calendar data for read-only offline access
 */
// Helper to load cached data from localStorage
function loadCachedData(calendarId) {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.calendarId === calendarId) {
        return parsed.data;
      }
    }
  } catch (err) {
    console.warn('Failed to load offline cache:', err);
  }
  return null;
}

export function useOfflineCache(calendarId) {
  // Use lazy initializer to load cached data synchronously on first render
  const [cachedData, setCachedData] = useState(() => loadCachedData(calendarId));
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Reload cache when calendarId changes
  useEffect(() => {
    setCachedData(loadCachedData(calendarId));
  }, [calendarId]);

  // Track online/offline status
  useEffect(() => {
    function handleOnline() {
      setIsOffline(false);
    }

    function handleOffline() {
      setIsOffline(true);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update cache with fresh data
  const updateCache = useCallback((data) => {
    if (!calendarId || !data) return;

    try {
      const cacheData = {
        calendarId,
        data,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      setCachedData(data);
    } catch (err) {
      console.warn('Failed to update offline cache:', err);
    }
  }, [calendarId]);

  // Clear cache
  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(CACHE_KEY);
      setCachedData(null);
    } catch (err) {
      console.warn('Failed to clear offline cache:', err);
    }
  }, []);

  return {
    cachedData,
    isOffline,
    updateCache,
    clearCache,
  };
}

export default useOfflineCache;
